import './RoleStageCard.scss';
import Icon from 'components/atoms/Icon';
import { appSwitchRoutes } from 'constants/routes';
import { StageCardStatus } from 'enums/stageCardStatus.enum';
import {
  AssessmentStageKind,
  StageCandidateStatus,
  StageStatus,
} from 'generated/graphql';
import { parseStageStatus } from 'helpers/parseStageStatus';
import { differenceInCalendarDaysFns, isAfter } from 'helpers/time';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

export type TRoleStageCardProps = {
  roleId: string;
  stageId: string;
  kind: AssessmentStageKind;
  name: string;
  stageStatus: StageStatus;
  candidateStatus: StageCandidateStatus;
  endTime?: Date;
};

const stageKindDisplayName: Record<AssessmentStageKind, string> = {
  [AssessmentStageKind.Assessment]: 'stage.stageKind.assessment',
  [AssessmentStageKind.AiInterview]: 'stage.stageKind.aiInterview',
};

/** Same stage-kind iconography as the company app's hiring-process page. */
const stageKindIcon: Record<
  AssessmentStageKind,
  { icon: 'analytics' | 'ai_interview'; modifier: string }
> = {
  [AssessmentStageKind.Assessment]: {
    icon: 'analytics',
    modifier: 'Assessment',
  },
  [AssessmentStageKind.AiInterview]: {
    icon: 'ai_interview',
    modifier: 'AiInterview',
  },
};

const statusDisplayName: Record<StageCardStatus, string> = {
  [StageCardStatus.ACTIVE]: 'roles.stageStatus.active',
  [StageCardStatus.COMPLETED]: 'roles.stageStatus.completed',
  [StageCardStatus.EXPIRED]: 'roles.stageStatus.expired',
  [StageCardStatus.SCHEDULED]: 'roles.stageStatus.scheduled',
  [StageCardStatus.SIGNED_OFF]: 'roles.stageStatus.signedOff',
};

const RoleStageCard: FC<TRoleStageCardProps> = ({
  roleId,
  stageId,
  kind,
  name,
  stageStatus,
  candidateStatus,
  endTime,
}) => {
  const { t } = useTranslation();

  const status = parseStageStatus(stageStatus, candidateStatus);
  // Assessment stages show the company-configured stage name, since a role
  // can have several of them. AI interview stages keep the localised kind
  // label: they're capped at one per role, and wizard renames only touch the
  // AI-interview action's name, so the stage-level name can be stale.
  const stageName =
    kind === AssessmentStageKind.Assessment && name
      ? name
      : t(stageKindDisplayName[kind]);
  const statusString = t(statusDisplayName[status]);
  const ariaLabel = t('roles.stageWithStatus', {
    stageName,
    status: statusString,
  });

  const isExpiryNotificationVisible =
    endTime &&
    isAfter(new Date(endTime), new Date()) &&
    differenceInCalendarDaysFns(new Date(endTime), new Date()) < 1;

  const content = (
    <>
      <div className="RoleStageCard__Info">
        <div
          className={`RoleStageCard__KindIcon RoleStageCard__KindIcon--${stageKindIcon[kind].modifier}`}
          aria-hidden
        >
          <Icon icon={stageKindIcon[kind].icon} />
        </div>
        <p className="RoleStageCard__Info__Name">{stageName}</p>
        {isExpiryNotificationVisible ? <Icon icon="flame" /> : null}
      </div>
      {status === StageCardStatus.ACTIVE ? (
        <div className="RoleStageCard__ArrowInCircle">
          <Icon icon="right_arrow" />
        </div>
      ) : status === StageCardStatus.COMPLETED ? (
        // Same treatment as the old /stages completed card: a green check in
        // a green circle. The status is conveyed to screen readers through
        // the card's aria-label.
        <div
          className="RoleStageCard__StatusCircle RoleStageCard__StatusCircle--Completed"
          aria-hidden
        >
          <Icon icon="green_check" />
        </div>
      ) : status === StageCardStatus.EXPIRED ? (
        <div
          className="RoleStageCard__StatusCircle RoleStageCard__StatusCircle--Expired"
          aria-hidden
        >
          <Icon icon="denied" />
        </div>
      ) : (
        <span
          className={`RoleStageCard__Status RoleStageCard__Status--${status.toLowerCase()}`}
        >
          {statusString}
        </span>
      )}
    </>
  );

  if (status !== StageCardStatus.ACTIVE) {
    return (
      <div className="RoleStageCard" aria-label={ariaLabel}>
        {content}
      </div>
    );
  }

  return (
    <Link
      to={appSwitchRoutes.roleStageActions.getUrl(roleId, stageId)}
      className="RoleStageCard RoleStageCard--Clickable"
      aria-label={ariaLabel}
    >
      {content}
    </Link>
  );
};

export default RoleStageCard;
